import { TranslationKey } from '../../../../types/translation/TranslationKey';
import React, { useContext, useMemo, useState } from 'react';
import { BinContext } from '../../../../context/BinContext';
import { useNavigate, useParams } from 'react-router-dom';
import { t } from '../../../../types/translation/Translator';
import { StockLocationContext } from '../../../../context/StockLocationContext';
import { BinStatusContext } from '../../../../context/BinStatusContext';
import BinStatusLabel from '../../Common/BinStatusLabel';
import dayjs from 'dayjs';
import { ProductContext } from '../../../../context/ProductContext';
import { LotContext } from '../../../../context/LotContext';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { Product } from '../../../../types/product';
import { ProductInfoPaneTableHeaderKey } from '../../../../util/identifiers/ProductInfoPaneTableHeaderKey';
import Table from '../../../components/Table';
import { Routing } from '../../../../util/routes.util';
import GenerateBarcodeButton from '../../../components/Button/templates/GenerateBarcodeButton';
import { BarcodeExportEntityType } from '../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { BackButton, ExportIconButton } from '../../../components/Button';
import { UserContext } from '../../../../context/UserContext';
import { ProductExportModal } from '../Product/Modals/ProductExportModal';
import SearchBarWithFilter from '../../../../VentoryUI/components/common/SearchBarWithFilter';
import { ProductFilter } from '../../../../VentoryUI/components/filters/ProductFilter';
import { TagRelationContext } from '../../../../context/TagRelationContext';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { ProductFilterType } from '../../../../VentoryUI/components/filters/ProductFilter/ProductFilter';
import { TableSettingsContext } from '../../../../context/TableSettingsContext';
import { EntityType } from '../../../../types/comment';
import { CompanyContext } from '../../../../context/CompanyContext';
import { UnitOfMeasure } from '../../../../types/uom';
import { ContainerContext } from '../../../../context/ContainerContext';
import { ContainerTypeContext } from '../../../../context/ContainerTypeContext';

export default function BinDetailPane() {
  const id = useParams()['id'] || '';

  const navigate = useNavigate();

  if (!id) return null; // TODO: Entity not found

  const { bins, binsLoading } = useContext(BinContext);

  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { containers } = useContext(ContainerContext);
  const { containerTypes } = useContext(ContainerTypeContext);
  const { products, productsLoading } = useContext(ProductContext);
  const { lots } = useContext(LotContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { locale } = useContext(UserContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { getTableSettings } = useContext(TableSettingsContext);

  const tableSettings = getTableSettings(EntityType.product, currentCompany.id);
  const containerTableSettings = getTableSettings(EntityType.container, currentCompany.id);

  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);

  const bin = bins.get(id);

  const allItems = useMemo(() => {
    return [...products.values()]
      .map(v => [...v.values()])
      .flat()
      .filter(item => item.binId === bin?.id);
  }, []);

  const [items, setItems] = useState<Product[]>(allItems);

  const hasLPN = !!allItems.filter(item => item.lpn).length;
  const hasSerial = !!allItems.filter(item => item.serial).length;
  const hasLot = !!allItems.filter(item => item.lotId).length;

  const headers = [
    {
      key: 'productName',
      name: t(TranslationKey.productName).singular.label,
      text: (item: Product) => productMasterData.get(item.productMasterDataId)?.productName || 'Unknown Product Name',
    },
    {
      key: 'productNumber',
      name: t(TranslationKey.productNumber).singular.label,
      text: (item: Product) =>
        productMasterData.get(item.productMasterDataId)?.productNumber || 'Unknown Product Number',
    },
    {
      key: 'lpn',
      name: t(TranslationKey.lpn).singular.label,
      text: (item: Product) => item.lpn || '',
    },
    {
      key: 'serialNumber',
      name: t(TranslationKey.serial).singular.label,
      text: (item: Product) => item.serial || '',
    },
    {
      key: 'lotNumber',
      name: t(TranslationKey.lotNumber).singular.label,
      text: (item: Product) => lots.get(item.lotId || '')?.number || '',
    },
    {
      key: ProductInfoPaneTableHeaderKey.lotExpiry,
      name: t(TranslationKey.expirationDate).singular.label,
      text: (item: Product) =>
        lots.get(item.lotId || '')?.expirationDate
          ? dayjs(lots.get(item.lotId || '')?.expirationDate)
              .toDate()
              .toLocaleDateString(locale)
          : '',
    },
    {
      key: 'quantity',
      sortValue: (item: Product) => item.unitQuantity,
      name: t(TranslationKey.quantity).singular.label,
      text: (item: Product) =>
        UnitOfMeasure.toQuantityWithSuffix(
          productMasterData.get(item.productMasterDataId)?.unitOfMeasure,
          item.unitQuantity,
        ),
    },
  ].filter(item => {
    if (item.key === 'serialNumber' && !hasSerial) return false;
    if (item.key === 'lpn' && !hasLPN) return false;
    if (item.key === 'lotNumber' && !hasLot) return false;
    if (item.key === 'lotExpiry' && !hasLot) return false;

    return true;
  });

  const filter = new ProductFilter(
    stockLocations,
    productMasterData,
    bins,
    binStatuses,
    containers,
    containerTypes,
    tagRelations,
    customFields,
    new Map(allItems.map(i => [i.id, i])),
    tableSettings,
    containerTableSettings,
    ProductFilterType.detail,
    currentCompany,
  );

  const loading = binsLoading || productMasterDataLoading || productsLoading;

  return (
    <>
      {exportModalOpen && <ProductExportModal open={exportModalOpen} setOpen={setExportModalOpen} binId={bin?.id} />}

      <div className='content-x content-y-screen flex flex-grow flex-col gap-4'>
        <div className='grid grid-cols-3'>
          <div>
            <p className='font-semibold'>{t(TranslationKey.bin).singular.label}</p>
            <p
              className='mt-2 line-clamp-2 w-fit cursor-pointer text-xl font-bold text-gray-400'
              style={{ lineClamp: 2 }}
            >
              {bin?.name}
            </p>
          </div>
          <div className='justify-self-center'>
            <p className='font-semibold'>{t(TranslationKey.stockLocation).singular.label}</p>
            <p className='mt-2 line-clamp-2 w-fit cursor-pointer text-xl font-bold text-gray-400'>
              {stockLocations.get(bin?.stockLocationId || '')?.name || ''}
            </p>
          </div>
          <div className='justify-self-end'>
            <p className='font-semibold'>{t(TranslationKey.binStatus).singular.label}</p>
            <BinStatusLabel status={binStatuses.get(bin?.binStatusId || '')} />
          </div>
        </div>
        <div className='flex justify-between gap-2'>
          <SearchBarWithFilter
            loading={loading}
            items={allItems}
            setItems={setItems}
            placeholder={t(TranslationKey.filterProducts).singular.label}
            filter={filter}
          />
          <div className='flex gap-2'>
            {allItems.length > 0 && <ExportIconButton onClick={() => setExportModalOpen(true)} />}
          </div>
        </div>
        <Table
          filter={filter}
          loading={productsLoading || productMasterDataLoading}
          title={t(TranslationKey.product).plural.label}
          headers={headers}
          totalItemCount={allItems.length}
          items={items}
        />
        <div className='flex justify-end gap-2'>
          <BackButton onClick={() => navigate(Routing.stock.bins.overview())} />
          <GenerateBarcodeButton
            entityType={BarcodeExportEntityType.bin}
            entityIds={[id]}
            text={'Generate bin barcode'}
          />
        </div>
      </div>
    </>
  );
}
